import type { ReactNode } from "react";

interface CodeFormProps {
  title: string;
  description: ReactNode;
  loading: boolean;
  error: string;
  resending?: boolean;
  resendMessage?: string;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
  submitLabel?: string;
}

export default function CodeForm({
  title,
  description,
  loading,
  error,
  resending = false,
  resendMessage = "",
  onSubmit,
  onResend,
  onBack,
  submitLabel = "Verificar código",
}: CodeFormProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
        <i className="ri-shield-keyhole-line text-xl leading-none" aria-hidden="true"></i>
      </span>
      <h1 className="mt-4 text-xl font-heading font-bold text-foreground-950">{title}</h1>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{description}</p>

      <form
        className="mt-6"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          const code = String(new FormData(event.currentTarget).get("codigo") ?? "").trim();
          onSubmit(code);
        }}
      >
        <label htmlFor="codigo" className="mb-1.5 block text-sm font-label font-medium text-foreground-700">
          Código de 6 dígitos
        </label>
        <input
          id="codigo"
          name="codigo"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-center text-2xl font-label tracking-[0.5em] text-foreground-900 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        />

        {error && (
          <p role="alert" className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
            {error}
          </p>
        )}
        {resendMessage && (
          <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-3.5 py-3 text-sm text-accent-900">
            <i className="ri-checkbox-circle-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
            {resendMessage}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="mt-6 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-base font-label font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground-950"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base leading-none" aria-hidden="true"></i>
              Verificando…
            </>
          ) : (
            submitLabel
          )}
        </button>
      </form>

      <div className="mt-5 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onResend}
          disabled={resending}
          className="cursor-pointer text-sm font-label font-medium text-primary-700 transition-colors hover:text-primary-800 disabled:opacity-50"
        >
          {resending ? "Reenviando…" : "Reenviar código"}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="flex cursor-pointer items-center gap-1 text-sm font-label font-medium text-foreground-500 transition-colors hover:text-foreground-800"
        >
          <i className="ri-arrow-left-line text-base leading-none" aria-hidden="true"></i>
          Volver
        </button>
      </div>
    </div>
  );
}